/**
 * Defensive cleanup for clients that ran a previous version with the SW
 * registered. When the master `pwa.enabled` flag flips off (or stays off
 * on a returning visitor), we unregister all service workers + delete
 * their cache buckets so the next render is a vanilla SPA again.
 *
 * Idempotent — safe to call when no SW exists (returns 0). Returns the
 * number of unregistered SW and deleted caches for telemetry.
 */
export interface CleanupRuntime {
  readonly navigator: Navigator;
  readonly caches?: CacheStorage;
}

export interface CleanupReport {
  readonly unregisteredCount: number;
  readonly deletedCacheCount: number;
}

export async function cleanupLegacyServiceWorker(
  runtime: CleanupRuntime = window,
): Promise<CleanupReport> {
  const unregisteredCount = await unregisterAll(runtime);
  const deletedCacheCount = await deleteAllCaches(runtime);
  return { unregisteredCount, deletedCacheCount };
}

async function unregisterAll(runtime: CleanupRuntime): Promise<number> {
  if (!("serviceWorker" in runtime.navigator)) return 0;
  try {
    const registrations =
      await runtime.navigator.serviceWorker.getRegistrations();
    const results = await Promise.all(
      registrations.map((registration) => registration.unregister()),
    );
    return results.filter(Boolean).length;
  } catch {
    return 0;
  }
}

async function deleteAllCaches(runtime: CleanupRuntime): Promise<number> {
  if (!runtime.caches) return 0;
  try {
    const keys = await runtime.caches.keys();
    const results = await Promise.all(
      keys.map((key) => runtime.caches!.delete(key)),
    );
    return results.filter(Boolean).length;
  } catch {
    return 0;
  }
}
